import { Link } from "@tanstack/react-router";
import { resolveImage } from "@/lib/image-map";
import type { Product, Creator } from "@/lib/queries";

interface Props {
  products: Product[];
  creator?: Creator;
}

export function MacbookMockup({ products, creator }: Props) {
  const placeholders: Product[] = Array.from({ length: 6 }).map((_, i) => ({
    id: `mb-placeholder-${i}`,
    title: ["Cashmere crew", "Linen tote", "Silk slip dress", "Ceramic carafe", "Wool throw", "Oak stool"][i],
    brand: ["Atelier", "Maison", "Studio", "Heritage", "Nordic", "Workshop"][i],
    price: [180, 95, 240, 65, 220, 320][i],
    image_key: "",
    network: "levanta",
    in_stock: true,
  } as unknown as Product));
  const source = products.length ? products : placeholders;
  const offset = Math.min(3, Math.max(0, source.length - 6));
  const rotated = [...source.slice(offset), ...source.slice(0, offset)];
  const grid = rotated.slice(0, 6).reverse();
  const url = `pluggz.com / ${creator ? `@${creator.handle}` : "shop"}`;

  return (
    <div
      className="relative w-full h-full flex items-center justify-center"
      style={{ contain: "layout paint" }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[50%] h-[55%] w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/20 blur-[80px]"
      />

      <div className="relative" style={{ width: "min(96%, 720px)", maxWidth: "100%", aspectRatio: "1203 / 753" }}>
        <svg
          aria-label="Safari browser"
          fill="none"
          viewBox="0 0 1203 753"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute inset-0 h-full w-full drop-shadow-xl pointer-events-none"
        >
          <g clipPath="url(#safari-clip)">
            <path className="fill-ink" d="M0 52H1202V741C1202 747.627 1196.63 753 1190 753H12C5.37258 753 0 747.627 0 741V52Z" />
            <path className="fill-ink" clipRule="evenodd" d="M0 12C0 5.37258 5.37258 0 12 0H1190C1196.63 0 1202 5.37258 1202 12V52H0L0 12Z" fillRule="evenodd" />
            <path className="fill-card" clipRule="evenodd" d="M1.06738 12C1.06738 5.92487 5.99225 1 12.0674 1H1189.93C1196.01 1 1200.93 5.92487 1200.93 12V51H1.06738V12Z" fillRule="evenodd" />
            <circle className="fill-destructive/70" cx="27" cy="25" r="6" />
            <circle className="fill-zap/80" cx="47" cy="25" r="6" />
            <circle className="fill-accent/70" cx="67" cy="25" r="6" />
            <path className="fill-muted" d="M286 17C286 13.6863 288.686 11 292 11H946C949.314 11 952 13.6863 952 17V35C952 38.3137 949.314 41 946 41H292C288.686 41 286 38.3137 286 35V17Z" />
            <path d="M566.269 32.0852H572.426C573.277 32.0852 573.696 31.6663 573.696 30.7395V25.9851C573.696 25.1472 573.353 24.7219 572.642 24.6521V23.0842C572.642 20.6721 571.036 19.5105 569.348 19.5105C567.659 19.5105 566.053 20.6721 566.053 23.0842V24.6711C565.393 24.7727 565 25.1917 565 25.9851V30.7395C565 31.6663 565.418 32.0852 566.269 32.0852ZM567.272 22.97C567.272 21.491 568.211 20.6785 569.348 20.6785C570.478 20.6785 571.423 21.491 571.423 22.97V24.6394L567.272 24.6458V22.97Z" className="fill-muted-foreground" />
            <text className="fill-muted-foreground" fontFamily="Inter, sans-serif" fontSize="14" x="582" y="31">{url}</text>
          </g>
          <defs>
            <clipPath id="safari-clip">
              <rect fill="white" height="753" width="1203" />
            </clipPath>
          </defs>
        </svg>

        {/* HTML viewport overlay — avoids Safari/iOS foreignObject rendering bugs */}
        <div
          className="absolute overflow-hidden bg-background"
          style={{
            left: "0.083%",
            top: "6.906%",
            right: "0.083%",
            bottom: "0%",
            borderBottomLeftRadius: "0.95% / 1.5%",
            borderBottomRightRadius: "0.95% / 1.5%",
          }}
        >
          <div className="flex items-center justify-between px-4 py-3 sm:px-8 sm:py-4">
            <p className="font-serif text-base sm:text-2xl tracking-tight">Pluggz.</p>
            <div className="flex items-center gap-2 sm:gap-3">
              <p className="hidden sm:inline text-[12px] uppercase tracking-widest text-muted-foreground">Editor's edit</p>
              <div className="flex items-center gap-1.5 rounded-full bg-destructive/10 px-2 py-0.5 sm:px-2.5 sm:py-1">
                <span className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-destructive animate-pulse" />
                <span className="text-[8px] sm:text-[10px] uppercase tracking-widest text-destructive">Live</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 px-3 pb-3 sm:gap-4 sm:px-6 sm:pb-6">
            {grid.map((p, i) => (
              <Link
                key={`${p.id}-${i}`}
                to="/product/$id"
                params={{ id: p.id }}
                aria-label={`${p.title} by ${p.brand}, £${p.price}`}
                tabIndex={i < products.length ? 0 : -1}
                aria-hidden={i >= products.length ? true : undefined}
                className="block overflow-hidden rounded-md sm:rounded-lg border border-border/40 bg-card"
              >
                <div className="relative aspect-[5/4] overflow-hidden bg-muted">
                  <img
                    src={resolveImage(p.image_key)}
                    alt={p.title}
                    loading={i < 3 ? "eager" : "lazy"}
                    decoding="async"
                    className="h-full w-full object-cover"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
                  />
                </div>
                <div className="flex items-center justify-between px-2 py-1.5 sm:px-3 sm:py-2">
                  <div className="min-w-0">
                    <p className="truncate text-[8px] sm:text-[10px] uppercase tracking-wider text-muted-foreground">{p.brand}</p>
                    <p className="truncate font-serif text-[10px] sm:text-sm leading-tight">{p.title}</p>
                  </div>
                  <p className="shrink-0 font-serif text-[10px] sm:text-sm">{p.price ? `£${p.price}` : ""}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}